/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useRouter } from 'next/router';

import axios from 'axios';

import { useToast } from './toast';
import { ILoggedUser } from '../interfaces/i-logged-user';
import { IFCWithChildren } from '../interfaces/i-fc-with-childrent';
import { useCookies } from '../hooks/use-cookies';
import { toUserTypesEnum } from '../enums/user-types.enum';
import { cookies as cookieKeys } from '../constants/cookies';
import { UserDocumentModal } from '../components/UserDocumentModal';
import { SplashLoader } from '../components/SplashLoader';
import { api } from '../api';

interface IAuthContextData {
  user: ILoggedUser;
  loggedIn: boolean;
  loading: boolean;
  savedPrompts: string[];
  adminView: boolean;
  hideDescriptions: boolean;
  login: (username: string, password: string) => Promise<ILoggedUser>;
  loginWithGoogle: (options?: { redirect?: string; returnTo?: string }) => void;
  logout: () => void;
  includeSavedPrompts: (promptId: string) => void;
  excludeSavedPrompts: (promptId: string) => void;
  setAdminView: (value: boolean) => void;
  setHideDescriptions: (value: boolean) => void;
}

const AuthContext = createContext({} as IAuthContextData);

const AuthProvider: IFCWithChildren = ({ children }) => {
  const router = useRouter();
  const { showToast } = useToast();
  const { getItem, setItem } = useCookies();
  const [loading, setLoading] = useState(true);
  const [savedPrompts, setSavedPrompts] = useState<string[]>([]);
  const [user, setUser] = useState<ILoggedUser | null>({} as ILoggedUser);
  const [showUserDocumentNeedModal, setShowUserDocumentNeedModal] =
    useState(false);

  const parsedAdminSettings = useMemo(() => {
    try {
      const raw = getItem(cookieKeys.adminSettings);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, [getItem]);

  const [adminView, setAdminViewState] = useState<boolean>(
    parsedAdminSettings.adminView === true
  );
  const [hideDescriptions, setHideDescriptionsState] = useState<boolean>(
    parsedAdminSettings.hideDescriptions === true
  );

  const persistAdminSettings = useCallback(
    (settings: { adminView: boolean; hideDescriptions: boolean }) => {
      setItem(cookieKeys.adminSettings, JSON.stringify(settings));
    },
    [setItem]
  );

  const setAdminView = useCallback(
    (value: boolean) => {
      setAdminViewState(value);
      persistAdminSettings({ adminView: value, hideDescriptions });
    },
    [hideDescriptions, persistAdminSettings]
  );

  const setHideDescriptions = useCallback(
    (value: boolean) => {
      setHideDescriptionsState(value);
      persistAdminSettings({ adminView, hideDescriptions: value });
    },
    [adminView, persistAdminSettings]
  );

  const includeSavedPrompts = useCallback(
    (promptId: string) => {
      setSavedPrompts((prevPrompts) => [...prevPrompts, promptId]);
    },
    [setSavedPrompts]
  );

  const excludeSavedPrompts = useCallback(
    (promptId: string) => {
      setSavedPrompts((prevPrompts) =>
        prevPrompts.filter((id) => id !== promptId)
      );
    },
    [setSavedPrompts]
  );

  const updateUserDocument = useCallback(
    async (document: string) => {
      try {
        await api.post('/auth/document', {
          cpf: document,
        });

        setUser((prevUser) => ({
          ...prevUser,
          needUserDocument: false,
        }));

        showToast({
          severity: 'success',
          title: 'Sucesso',
          description: 'Documento atualizado com sucesso.',
        });
        setShowUserDocumentNeedModal(false);

        return;
      } catch (error) {
        const apiMessage = axios.isAxiosError(error)
          ? error.response?.data?.message
          : null;

        const description = Array.isArray(apiMessage)
          ? apiMessage.join(' ')
          : apiMessage || 'Nao foi possivel atualizar o documento.';

        throw new Error(description);
      }
    },
    [showToast]
  );

  const activateUserDocumentNeedModal = useCallback(() => {
    setShowUserDocumentNeedModal(true);
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<ILoggedUser> => {
      const { data: response } = await api.post<ILoggedUser>('/auth/login', {
        email,
        password,
      });

      setUser({
        ...response,
        type: toUserTypesEnum(response.type),
      });

      return response;
    },
    []
  );

  const loginWithGoogle = useCallback(
    (options?: { redirect?: string; returnTo?: string }) => {
      if (typeof window === 'undefined' || !api.defaults.baseURL) {
        return;
      }

      const url = new URL('/auth/google', api.defaults.baseURL);

      if (options?.redirect) {
        url.searchParams.set('redirect', options.redirect);
      }

      if (options?.returnTo) {
        url.searchParams.set('returnTo', options.returnTo);
      }

      window.location.assign(url.toString());
    },
    []
  );

  const logout = useCallback(async () => {
    await api.post('/auth/logout');
    setUser({} as ILoggedUser);
    router.push('/login');
  }, [router]);

  const refresh = useCallback(async () => {
    try {
      const { data: response } = await api.get<ILoggedUser>('/auth/validate');
      setUser({
        ...response,
        type: toUserTypesEnum(response.type),
      });

      try {
        const { data: saved } = await api.get<{ data: string[] }>(
          '/saved-prompts'
        );
        setSavedPrompts(saved.data ?? []);
      } catch {
        // silently ignore if endpoint fails
      }

      const privateRoutes = [
        'prompts',
        'categories',
        'users',
        'account',
        'saved',
        'training',
      ];

      const isPrivate = privateRoutes.some((route) => {
        const toCheck = router.asPath.includes('?')
          ? router.asPath.split('?')[0]
          : router.asPath;

        return (
          toCheck.replace('/', '').includes(route) && !toCheck.includes('login')
        );
      });

      if (isPrivate && response.needUserDocument) {
        activateUserDocumentNeedModal();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [router, activateUserDocumentNeedModal]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        loggedIn: !!user?.id,
        loading,
        user: user as ILoggedUser,
        savedPrompts,
        adminView,
        hideDescriptions,
        excludeSavedPrompts,
        includeSavedPrompts,
        login,
        loginWithGoogle,
        logout,
        setAdminView,
        setHideDescriptions,
      }}
    >
      <UserDocumentModal
        close={() => setShowUserDocumentNeedModal(false)}
        open={showUserDocumentNeedModal}
        onSubmit={updateUserDocument}
      />
      {loading ? <SplashLoader /> : <>{children}</>}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
